/* global document */
import { parseHTML } from 'linkedom';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import suitcss from '../../../helpers/suitcss';

import { copyPropTypes } from '../../../propTypes/typography';
import TextRaw from './TextRaw';
import { ariaAttributes, ariaHtmlAttributes, ariaPropsTypes } from '../../../helpers/ariaAttributes';

const setAriaLabelForInfoILinksInLists = (html, dialogTemplates, prefix = '') => {
  if (!html) return html;

  // check if html is object or react component or something other than string
  // return without modification so that component doesn't break
  if (typeof html !== 'string') {
    return html;
  }

  // check if is html string
  if (!html.startsWith('<') && !html.endsWith('>')) {
    return html;
  }

  const processDocument = (doc, isSSR = false) => {
    // the tariffcards are still using the old markup with anchors (selectorOld)
    // in the future they should be using the new markup with buttons (selectorNew) for better a11y
    // but for now we need to support both

    const selectorOld = 'a[href*="dbn.showDialog"] img[src*="info-icon.svg"]';
    const selectorNew = 'button[onclick*="dbn.showDialog"] img[src*="info-icon.svg"]';
    let finalSelector = selectorOld;
    let triggerTag = 'a';
    let actionAttribute = 'href';

    // if the old selector does not match, use new selector
    if (doc.querySelectorAll(selectorOld).length === 0) {
      triggerTag = 'button';
      actionAttribute = 'onclick';
      finalSelector = selectorNew;
    }

    doc.querySelectorAll(finalSelector).forEach((img) => {
        img.setAttribute('alt', '');
        const triggerElement = img.closest(triggerTag); // triggerElement: button|a
        if (!triggerElement) return;

        const dialogKey = triggerElement.getAttribute(actionAttribute)?.match(/dbn\.showDialog\('([^']+)'\)/)?.[1];
        const headline = dialogTemplates[dialogKey]?.headline;
        if (!dialogKey || !headline) return;

        const ariaLabel = prefix ? `${prefix} ${headline}` : headline;

        if (triggerTag === 'a') {
          // replace anchor with button and add image as child
          // if ssr the doc is the actual document
          const button = isSSR ? doc.createElement('button') : document.createElement('button');
          button.setAttribute('aria-label', ariaLabel);
          button.setAttribute('onclick', triggerElement.href.replace(/javascript:/, ''));
          button.appendChild(img);
          triggerElement.parentNode.replaceChild(button, triggerElement);
        } else {
          triggerElement.setAttribute('aria-label', ariaLabel);
          triggerElement.setAttribute('onclick', triggerElement.getAttribute('onclick').replace(/javascript:/, ''));
        }
      });
  };

  try {
    // if we are in a browser environment, we can use html template
    // otherwise we need to use the linkedom library to parse the html
    if (typeof document !== 'undefined') {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      processDocument(template.content);
      return template.innerHTML;
    } else {
      const rootElementClass = 'ssr-parsing-root-element';

      // add root element because the text that comes from other components
      // or the cms may have multiple root elements
      const { document } = parseHTML(`<div class="${rootElementClass}">${html.trim()}</div>`);
      processDocument(document, true);

      // return only the innerHTML of the root element
      return document.querySelector(`.${rootElementClass}`).innerHTML;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error parsing HTML:', err, html);

    // if we can't process the html return original version so the app doesn't break
    return html;
  }
};

function TextCopy(props) {
  // styling only per utility classes -
  // props are only to get sure, that unrefactored elements will work
  const utilities = [
    props.uppercase && 'uppercase',
    props.inverted && 'colorInverted',
    props.condensed && 'fontCondensed',
    ...props.utilities,
  ];

  const className = suitcss({
    className: props.className,
    componentName: 'TextCopy',
    modifiers: [
      props.size,
      props.embedded && 'embedded',
      props.raw && 'raw',
    ],
    utilities,
  });

  const {
    onClick,
    role,
    id,
    raw,
    postfix,
    element,
    children,
    dialogTemplates,
    ui,
  } = props;

  const modifiedChildren = Array.isArray(children)
    ? children.map((child) =>
        setAriaLabelForInfoILinksInLists(child, dialogTemplates, ui.guiWordLearnMoreAbout))
    : setAriaLabelForInfoILinksInLists(children, dialogTemplates, ui.guiWordLearnMoreAbout);

  if (raw && !postfix) {
    return (
      <TextRaw
        className={className}
        element={element === 'p' ? 'div' : element}
        onClick={onClick}
        {...ariaAttributes(props)}
        role={role}
        id={id}
      >
        {modifiedChildren}
      </TextRaw>
    );
  }

  return (
    <props.element
      className={className}
      onClick={onClick}
      {...ariaHtmlAttributes(props)}
      role={role}
      id={id}
    >
      {!props.postfix && modifiedChildren}
      {props.postfix && props.raw && (<TextRaw>{modifiedChildren}</TextRaw>)}
      {props.postfix && !props.raw && (<span>{modifiedChildren}</span>)}
      {props.postfix && (<span>{props.postfix}</span>)}
    </props.element>
  );
}

TextCopy.propTypes = {
  ...copyPropTypes,
  ...ariaPropsTypes,
  role: PropTypes.string,
  id: PropTypes.string,
  dialogTemplates: PropTypes.object,
  ui: PropTypes.object,
};

TextCopy.defaultProps = {
  size: 'default',
  element: 'p',
  raw: false,
  utilities: [],
};

const mapStateToProps = (state) => ({
  dialogTemplates: state.site.dialogTemplates,
  ui: state.ui,
});

export default connect(mapStateToProps)(TextCopy);
